/**
 * One source's principle funnel (spec account-persona 10 U4): its principles typed by class, the rules held now, their
 * checks with the verdict before and since the rule was stated, and what the finance brain routed of them. Template
 * example: NextAdmin `(dashboard)/(home)/_component/top-products` (a `Card` of `ItemList` rows), as the call funnel.
 */
import type React from 'react';
import { Badge, Card, ItemList, type ItemListItem } from '../../../components/ui/index.js';
import type { ReasoningPrincipleFunnel } from '../../../generated/api/types.gen.js';
import { HYPOTHESIS_LABEL, PRINCIPLE_CLASS_LABEL, VERDICT_LABEL, WINDOW_LABEL } from './reasoning-format.js';

const share = (part: number, whole: number) => (whole > 0 ? ` (${Math.round((part / whole) * 100)} %)` : '');

const breakdown = (counts: Record<string, number>, labels: Record<string, string> = {}) =>
  Object.entries(counts)
    .sort(([, a], [, b]) => b - a)
    .map(([key, n]) => `${labels[key] ?? key} ${n}`)
    .join(' · ') || 'none';

const total = (counts: Record<string, number>) => Object.values(counts).reduce((sum, n) => sum + n, 0);

export const ReasoningPrincipleFunnelCard: React.FC<{ funnel: ReasoningPrincipleFunnel }> = ({ funnel }) => {
  const items: ItemListItem[] = [
    {
      id: 'principles',
      title: 'Principles',
      subtitle: `${funnel.typed} typed, ${funnel.rejected} refused by the typing pass, ${funnel.untyped} not typed yet`,
      value: funnel.principles,
    },
    {
      id: 'classes',
      title: 'Typed, by class',
      subtitle: breakdown(funnel.byClass, PRINCIPLE_CLASS_LABEL),
      value: funnel.typed,
    },
    {
      id: 'held',
      title: 'Rules held now',
      subtitle: 'One per rule: a rule said again is evidence for it, not a new rule',
      value: funnel.held,
    },
    {
      id: 'checked',
      title: 'Checked (latest check)',
      subtitle: breakdown(funnel.checks, VERDICT_LABEL),
      value: `${funnel.checked}${share(funnel.checked, funnel.held)}`,
    },
    {
      id: 'in-sample',
      title: 'Before it was stated (in sample)',
      subtitle: `What the account could see: ${breakdown(funnel.inSample, WINDOW_LABEL)}`,
      value: total(funnel.inSample),
    },
    {
      id: 'out-of-sample',
      title: 'Since it was stated (out of sample)',
      subtitle: `The verdict the finance brain believes: ${breakdown(funnel.outOfSample, WINDOW_LABEL)}`,
      value: total(funnel.outOfSample),
    },
    ...(funnel.module
      ? [
          {
            id: 'routed',
            title: 'Routed to research',
            subtitle: `Hypotheses: ${breakdown(funnel.module.hypotheses, HYPOTHESIS_LABEL)}`,
            value: funnel.module.routed,
          },
        ]
      : []),
  ];
  return (
    <Card
      title={funnel.handle}
      actions={funnel.untyped > 0 ? <Badge variant="warning">{funnel.untyped} not typed yet</Badge> : undefined}
      className="xl:col-span-6"
    >
      <ItemList items={items} />
    </Card>
  );
};
